import { alpha, createTheme } from '@mui/material/styles';

/** Vivid royal blue — paired with a warm coral-orange accent, a classic energetic
 *  airline/travel combo (THY, Pegasus, ...) modernized with richer, more saturated
 *  hues than the previous flat corporate blue. Kept as the single source of truth for
 *  every hand-mixed `alpha(...)` tint below so the palette stays internally consistent. */
const PRIMARY = '#2954E8';
const PRIMARY_DARK = '#173AAE';
const PRIMARY_LIGHT = '#6E8CF2';
const SECONDARY = '#FF7A45';
const SECONDARY_DARK = '#E85D26';
const INK = '#16233D';
const INK_SOFT = '#5B6B85';
const BORDER = '#E4E9F5';
const SURFACE = '#F5F7FC';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: PRIMARY, dark: PRIMARY_DARK, light: PRIMARY_LIGHT, contrastText: '#ffffff' },
    secondary: { main: SECONDARY, dark: SECONDARY_DARK, contrastText: '#ffffff' },
    background: { default: SURFACE, paper: '#ffffff' },
    divider: BORDER,
    text: { primary: INK, secondary: INK_SOFT },
    success: { main: '#1FA971' },
    warning: { main: '#F0A93A' },
    error: { main: '#E4483B' },
    info: { main: '#2E90D6' },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: ['"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    h4: { fontWeight: 800, letterSpacing: -0.3 },
    h5: { fontWeight: 700, letterSpacing: -0.2 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, paddingInline: 16 },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundImage: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
            '&:hover': { backgroundImage: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_DARK} 100%)` },
          },
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: PRIMARY,
          backgroundImage: `linear-gradient(90deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 55%, ${PRIMARY_LIGHT} 130%)`,
          boxShadow: `0 2px 12px ${alpha(PRIMARY_DARK, 0.25)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: `1px solid ${BORDER}`,
          boxShadow: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderLeft: '3px solid transparent',
          transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
          '&.Mui-selected': {
            backgroundColor: alpha(PRIMARY, 0.1),
            borderLeftColor: SECONDARY,
            color: PRIMARY,
            fontWeight: 600,
            '& .MuiListItemIcon-root': { color: PRIMARY },
            '&:hover': { backgroundColor: alpha(PRIMARY, 0.15) },
          },
          '&:hover': { backgroundColor: alpha(PRIMARY, 0.06) },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 40, color: INK_SOFT },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderRadius: 16,
          borderColor: BORDER,
          boxShadow: `0 1px 2px ${alpha(INK, 0.04)}, 0 8px 24px ${alpha(INK, 0.05)}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: BORDER },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: alpha(PRIMARY, 0.06),
          color: INK,
          fontWeight: 700,
          fontSize: '0.8125rem',
          borderBottom: `2px solid ${alpha(PRIMARY, 0.18)}`,
          whiteSpace: 'nowrap',
        },
        root: {
          borderBottom: `1px solid ${BORDER}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: alpha(PRIMARY, 0.035) },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundImage: `linear-gradient(90deg, ${SECONDARY}, ${PRIMARY})`,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
  },
});
